import React, { useEffect, useState } from 'react';

// --- Typing Text Component ---
const TypingText = ({ text, speed = 50, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setDisplayedText('');
    setIndex(0);
  }, [text]);

  useEffect(() => {
    if (index >= text.length) {
      onComplete?.();
      return;
    }
    const timeout = setTimeout(() => {
      setDisplayedText((prev) => prev + text.charAt(index));
      setIndex((prev) => prev + 1);
    }, speed);
    return () => clearTimeout(timeout);
  }, [index, text, speed, onComplete]);

  return (
    <div
      style={{
        fontFamily: 'monospace',
        whiteSpace: 'pre-wrap',
        fontSize: 13,
        lineHeight: 1.7,
        color: '#1a1a2e',
      }}
    >
      {displayedText}
    </div>
  );
};

// --- Loader with Multicolor Dots ---
const DotsLoader = () => (
  <div style={{ display: 'flex', gap: '6px', padding: '10px 4px' }}>
    {[0, 1, 2, 3].map((dot) => (
      <div
        key={dot}
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          animation: 'pulse 1.4s infinite',
          animationDelay: `${dot * 0.2}s`,
          backgroundColor: ['#511D43', '#901E3E', '#DC2525', '#0D92F4'][dot],
        }}
      />
    ))}
    <style>
      {`
      @keyframes pulse {
        0%, 80%, 100% { transform: scale(0); opacity: 0.3; }
        40% { transform: scale(1); opacity: 1; }
      }
      `}
    </style>
  </div>
);

// --- Selectable suggestion row ---
const SuggestionRow = ({ value, isActive, onSelect, index }) => (
  <div
    onClick={() => onSelect && onSelect(value)}
    style={{
      cursor: onSelect ? 'pointer' : 'default',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '9px 12px',
      borderRadius: 8,
      border: isActive ? '1.5px solid rgb(255, 123, 41)' : '1px solid #D6D6D6',
      backgroundColor: isActive ? 'rgba(255, 123, 41, 0.07)' : '#FFFFFF',
      transition: 'border-color 0.15s, background-color 0.15s',
      animation: `fadeSlideIn 0.25s ease both`,
      animationDelay: `${index * 60}ms`,
    }}
    onMouseEnter={(e) => {
      if (!isActive) e.currentTarget.style.borderColor = 'rgb(255, 123, 41)';
    }}
    onMouseLeave={(e) => {
      if (!isActive) e.currentTarget.style.borderColor = '#D6D6D6';
    }}
  >
    <div
      style={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isActive ? 'rgb(255, 123, 41)' : '#F0F0F0',
        fontSize: 11,
        fontWeight: 600,
        color: isActive ? '#fff' : '#888',
        transition: 'background-color 0.15s, color 0.15s',
      }}
    >
      {index + 1}
    </div>
    <span
      style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 13,
        fontWeight: isActive ? 600 : 400,
        color: isActive ? 'rgb(24, 20, 54)' : '#333',
        transition: 'font-weight 0.15s',
      }}
    >
      {value}
    </span>
    {isActive && (
      <span
        style={{
          marginLeft: 'auto',
          fontSize: 11,
          fontWeight: 600,
          color: 'rgb(255, 123, 41)',
          letterSpacing: 0.3,
        }}
      >
        ✓ Selected
      </span>
    )}
  </div>
);

// --- Parent Component ---
const ChatGPTStyleMessage = (props) => {
  const { text, isLoading = false, suggestions = [], selectedValue, onSelectSuggestion } = props;

  const [typingDone, setTypingDone] = useState(false);

  useEffect(() => {
    setTypingDone(false);
  }, [text]);

  const hasSuggestions = !isLoading && suggestions?.length > 0;

  return (
    <div
      style={{
        width: '100%',
        borderRadius: 12,
        overflow: 'hidden',
        border: '1px solid rgb(255, 123, 41)',
        backgroundColor: '#FFF7F0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
      }}
    >
      <style>
        {`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        `}
      </style>

      {/* Header strip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 14px',
          borderBottom: '1px solid #FFE0BF',
          backgroundColor: '#FFF0E3',
        }}
      >
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'rgb(255, 123, 41)',
          }}
        />
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.6,
            color: 'rgb(255, 123, 41)',
            textTransform: 'uppercase',
          }}
        >
          AI Response
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: '12px 14px' }}>
        {isLoading ? (
          <DotsLoader />
        ) : (
          <>
            {text && <TypingText text={text} speed={30} onComplete={() => setTypingDone(true)} />}
            {!typingDone && text && <DotsLoader />}
          </>
        )}

        {/* Suggestions — only after typing animation completes */}
        {hasSuggestions && typingDone && (
          <div style={{ marginTop: 14 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: '#888',
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                marginBottom: 8,
              }}
            >
              Click to select a option
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {suggestions.map((s, idx) => {
                const value = s?.title || s;
                if (!value) return null;
                return (
                  <SuggestionRow
                    key={`${value}-${idx}`}
                    value={value}
                    index={idx}
                    isActive={selectedValue === value}
                    onSelect={onSelectSuggestion}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatGPTStyleMessage;
